import React, { FC, useMemo, memo } from 'react';
import { useSelector } from 'react-redux';

import { Campaign as CampaignType } from '@shared/types/mylounge';
import { getCategoryTabId } from '@client/redux/modules/mylounge';
import { getCampaignGridImage } from '@client/helpers/images';
import { Showstopper } from '@browsing/get-inspired';
import { TopPicks4U } from '@browsing/top-picks-4U';
import { getIsMobileScreen } from '@client/redux/modules/clientDevice';
import { getGender } from '@client/redux/modules/customer';
import { useVariant } from '@client/lib/assignments';
import { SalesChannel, useConfig } from '@browsing/config';
import { CampaignCard } from '../components/campaign-card';
import { getColumnCount, mergeCampaignsWithGrid, getGrid } from './helpers';
import { useGridTrack } from './hooks';
import { CustomerGender, GridBlockDimensions } from './constants';

import { Container, GridWrapper } from './styles';
import { ShowstopperContent } from '../components/showstopper';
import { ShowstopperGridContainer } from '../components/showstopper.styles';
import { CategoryTabIds } from '../../banner/constants';

interface Props {
    width: number;
    campaigns: CampaignType[];
    showstopper?: ShowstopperContent;
    isPlusEnabled: boolean;
}

export const Grid: FC<Props> = ({ width, campaigns, showstopper, isPlusEnabled }) => {
    const categoryTabId = useSelector(getCategoryTabId);
    const columnCount = useMemo(() => getColumnCount(width), [width]);
    const hasShowstopper = !!showstopper;
    useGridTrack(columnCount);
    const grid = useMemo(() => {
        return getGrid(categoryTabId, columnCount, campaigns.length, hasShowstopper);
    }, [categoryTabId, columnCount, campaigns.length, hasShowstopper]);

    const campaignsWithGrid = useMemo(
        () => mergeCampaignsWithGrid(campaigns, grid),
        [campaigns, grid],
    );

    let campaignIndex = 0; // only for upcoming campaings, banner position is not counted

    const isMobileScreen = useSelector(getIsMobileScreen);
    const gender = useSelector(getGender)?.toUpperCase() as CustomerGender;
    const config = useConfig();

    const isOmnibusRrpExperiment = useVariant(
        'omnibus_change_rrp_display',
        'variant_RRP_display_on',
    );
    const omnibusPolandOrItaly =
        config.salesChannel === SalesChannel.Poland || config.salesChannel === SalesChannel.Italy;

    if (!campaigns) {
        return null;
    }

    return (
        <Container>
            {categoryTabId === CategoryTabIds.ALL ? (
                <GridWrapper id="campaigns-open">
                    {campaignsWithGrid.map(gridBlock => {
                        const dimension = `${gridBlock.w}x${gridBlock.h}` as GridBlockDimensions;
                        const imageUrl = getCampaignGridImage(gridBlock as CampaignType, dimension);
                        if (!gridBlock.type) {
                            return (
                                <CampaignCard
                                    key={(gridBlock as CampaignType).campaignId}
                                    campaign={gridBlock as CampaignType}
                                    isPlusEnabled={isPlusEnabled}
                                    imageUrl={imageUrl}
                                    position={`${++campaignIndex}|${columnCount}`}
                                />
                            );
                        }
                        if (gridBlock.id === 'Showstopper_1' || gridBlock.id === 'Showstopper') {
                            return (
                                <ShowstopperGridContainer
                                    key={gridBlock.id}
                                    columnCount={columnCount}
                                >
                                    <TopPicks4U
                                        gender={gender}
                                        isOmnibusRrpExperiment={isOmnibusRrpExperiment}
                                        omnibusPolandOrItaly={omnibusPolandOrItaly}
                                    />
                                </ShowstopperGridContainer>
                            );
                        }
                        if (hasShowstopper && gridBlock.id === 'Showstopper_2') {
                            return (
                                <ShowstopperGridContainer
                                    key={gridBlock.id}
                                    columnCount={columnCount}
                                >
                                    <Showstopper
                                        title={showstopper.title}
                                        content={showstopper.content}
                                        media={showstopper.media}
                                        carouselItems={showstopper.campaigns}
                                        position={showstopper.position}
                                        categoryTabId={showstopper.categoryTabId}
                                        isMobileScreen={isMobileScreen}
                                        visitorID={showstopper.visitorID}
                                    />
                                </ShowstopperGridContainer>
                            );
                        }
                        return undefined;
                    })}
                </GridWrapper>
            ) : (
                <GridWrapper isSimpleGrid id="campaigns-open">
                    {campaigns.map(campaign => (
                        <CampaignCard
                            key={campaign.campaignId}
                            campaign={campaign}
                            isPlusEnabled={isPlusEnabled}
                            imageUrl={getCampaignGridImage(
                                campaign,
                                GridBlockDimensions.SQUARE_SMALL,
                            )}
                            position={`${++campaignIndex}|1`}
                        />
                    ))}
                </GridWrapper>
            )}
        </Container>
    );
};

export default memo(Grid);
